import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Spinner, Table } from 'reactstrap';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiTrashCanOutline, mdiEyeOutline } from '@mdi/js';
import { FileUtil } from '../../../../utils';
import { useExpenseClaimUtilCtx } from '../../../../pages/ExpenseClaims/context/ExpenseClaimUtilProvider';
import { downloadIcon2 } from '../../../../assets/index';
import DisplayModal from '../../../Common/DisplayModal/DisplayModal';

import './AttachmentTable.scss';

export default function AttachmentTable({
  claimNo,
  claimStatus,
  attachments,
  queryKey,
  attachmentsIsLoading,
  formType,
  finCoCd,
  ...props
}) {
  const queryClient = useQueryClient();
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);
  const { downloadAttachmentMutation, deleteExpenseClaimAttachmentMutation } =
    useExpenseClaimUtilCtx();
  const [fileLoading, setFileLoading] = useState({
    type: null,
    index: null,
  });

  const handleTicketDownloadAttachment = async item => {
    try {
      setFileLoading({ type: 'download', index: item?.ID });
      const attachmentBlob = await downloadAttachmentMutation.mutateAsync({
        type: 'Expense_Claim',
        fileName: item?.DocName,
        claimNo: claimNo || '',
        finCoCd: finCoCd || '',
      });
      FileUtil.downloadBlobFile(attachmentBlob, item?.DocName || 'attachment');
    } catch (error) {
      toast.error(error || 'Failed to download attachment.', {
        className: 'toast-error',
      });
    } finally {
      setFileLoading({ type: null, index: null });
    }
  };

  const handleTicketViewAttachment = async item => {
    try {
      setFileLoading({ type: 'view', index: item?.ID });
      const attachmentBlob = await downloadAttachmentMutation.mutateAsync({
        type: 'Expense_Claim',
        fileName: item?.DocName,
        claimNo: claimNo || '',
        finCoCd: finCoCd || '',
      });
      setDisplayModalFile(
        new File([attachmentBlob], item?.DocName, {
          type: attachmentBlob.type,
        }),
      );
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error(error || 'Failed to download attachment.', {
        className: 'toast-error',
      });
    } finally {
      setFileLoading({ type: null, index: null });
    }
  };

  const handleTicketDisplayAttachment = async item => {
    try {
      setFileLoading({ type: 'delete', index: item?.ID });
      await deleteExpenseClaimAttachmentMutation.mutateAsync({
        docType: 'Expense_Claim',
        fileName: item?.DocName,
        claimNo: claimNo || '',
        docId: item?.ID,
        claimStatus,
      });
      toast.success('Attachment deleted successfully.', {
        className: 'toast-success',
      });
      queryClient.invalidateQueries([queryKey, claimNo, 'expenseClaimDetails']);
    } catch (error) {
      toast.error(error || 'Failed to delete attachment.', {
        className: 'toast-error',
      });
    } finally {
      setFileLoading({ type: null, index: null });
    }
  };

  const renderAttachments = () => {
    if (attachmentsIsLoading) {
      return (
        <tr>
          <td colSpan="5" className="text-center py-2">
            <span>Loading attachments...</span>
          </td>
        </tr>
      );
    }
    if (!attachments || attachments.length === 0) {
      return (
        <tr>
          <td colSpan="3" className="text-center py-2">
            No attachments available.
          </td>
        </tr>
      );
    }
    return attachments?.map((item, index) => (
      <tr key={item.id}>
        <td className="py-2">{index + 1}</td>
        <td title={FileUtil.parseFileName(item?.DocName)} className="py-2">
          {FileUtil.parseFileName(item?.DocName)}
        </td>
        <td title={item?.UploadedBy} className="py-2">
          {item?.UploadedBy}
        </td>
        <td className="py-2">{item?.UploadedDate}</td>
        <td className="py-0 text-center align-middle">
          <button
            title="View Attachment"
            type="button"
            className="btn btn-link action-btn"
            onClick={() => handleTicketViewAttachment(item)}
          >
            {fileLoading.type === 'view' && fileLoading.index === item?.ID ? (
              <Spinner style={{ width: 20, height: 20, borderWidth: 2 }} />
            ) : (
              <Icon path={mdiEyeOutline} className="action-icon" />
            )}
          </button>

          <button
            title="Download Attachment"
            type="button"
            className="btn btn-link action-btn"
            onClick={() => handleTicketDownloadAttachment(item)}
          >
            {fileLoading.type === 'download' &&
            fileLoading.index === item?.ID ? (
              <Spinner style={{ width: 20, height: 20, borderWidth: 2 }} />
            ) : (
              <img src={downloadIcon2} alt="" className="action-dl-icon" />
            )}
          </button>

          {formType !== 'view' && (
            <button
              title="Delete Attachment"
              type="button"
              className="btn btn-link action-btn"
              onClick={() => handleTicketDisplayAttachment(item)}
            >
              {fileLoading.type === 'delete' &&
              fileLoading.index === item?.ID ? (
                <Spinner style={{ width: 20, height: 20, borderWidth: 2 }} />
              ) : (
                <Icon
                  path={mdiTrashCanOutline}
                  className="action-icon"
                  color="red"
                />
              )}
            </button>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <div className="expense-claim-attachment-table-outer">
      <Table className="expense-claim-attachment-table">
        <thead>
          <tr>
            <th className="py-2 ps-2 fw-semibold" width="5%">
              #
            </th>
            <th className="py-2 fw-semibold" width="30%">
              Document Name
            </th>
            <th className="py-2 fw-semibold" width="25%">
              Uploaded By
            </th>
            <th className="py-2 fw-semibold" width="20%">
              Date
            </th>
            <th className="py-2 fw-semibold text-center" width="20%">
              Action
            </th>
          </tr>
        </thead>
        <tbody
          style={{
            maxHeight: props?.maxHeight || '130px',
          }}
        >
          {renderAttachments()}
        </tbody>
      </Table>
      <DisplayModal
        isModalOpen={displayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
    </div>
  );
}
